import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCallById } from '../services/api';
import socket from '../services/socket';
import CallTimeline from '../components/CallTimeline';
import { SafetyBadge, CallStatusBadge } from '../components/StatusBadge';

const ACTION_LABELS = {
  visit_branch: 'Visit branch',
  send_link: 'Send KYC link',
  callback_agent: 'Callback / agent assistance',
};

function CallDetail() {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCall = async () => {
    try {
      const res = await getCallById(id);
      setCall(res.data.call);
      setEvents(res.data.events);
    } catch (err) {
      console.error('Failed to load call:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCall();
    const handleUpdate = (data) => {
      if (data.callId === id) loadCall();
    };
    socket.on('call_update', handleUpdate);
    socket.on('dtmf_received', handleUpdate);
    socket.on('call_completed', handleUpdate);
    socket.on('ivr_step', handleUpdate);
    return () => {
      socket.off('call_update', handleUpdate);
      socket.off('dtmf_received', handleUpdate);
      socket.off('call_completed', handleUpdate);
      socket.off('ivr_step', handleUpdate);
    };
  }, [id]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
        <p className="mt-2 text-muted">Loading call details...</p>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-exclamation-circle fs-1 text-muted"></i>
        <p className="mt-2">Call not found</p>
        <Link to="/logs" className="btn btn-outline-primary">Back to Logs</Link>
      </div>
    );
  }

  const duration = call.connected_at && call.disconnected_at
    ? Math.round((new Date(call.disconnected_at) - new Date(call.connected_at)) / 1000)
    : null;

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4">
        <Link to="/logs" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i> Back
        </Link>
        <div>
          <h4 className="fw-bold mb-0">Call Detail</h4>
          <small className="text-muted">ID: {call._id}</small>
        </div>
      </div>

      <div className="row g-4">
        {/* Call Info */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4">
                <i className="bi bi-person-badge me-2 text-primary"></i>
                {call.employee_name}
              </h5>

              <div className="row g-3">
                <div className="col-6">
                  <label className="text-muted small d-block">Phone</label>
                  <strong>{call.phone}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Call Status</label>
                  <CallStatusBadge status={call.call_status} />
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">KYC Status</label>
                  <SafetyBadge status={call.safety_status} />
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Duration</label>
                  <strong>{duration !== null ? `${duration}s` : '—'}</strong>
                </div>
                {call.selected_action && (
                  <div className="col-12">
                    <label className="text-muted small d-block">Selected Action</label>
                    <span className="badge bg-danger fs-6">
                      <i className="bi bi-check2-square me-1"></i>
                      {ACTION_LABELS[call.selected_action] || call.selected_action}
                    </span>
                  </div>
                )}
                <div className="col-6">
                  <label className="text-muted small d-block">Language</label>
                  <strong>{call.language === 'en' ? 'English' : 'Marathi'}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">IVR voice</label>
                  <strong>{call.voice === 'male' ? 'Male' : 'Female'}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Validation</label>
                  <strong>{call.validation_status || 'pending'}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Retry Attempts</label>
                  <strong>{call.connect_retry_attempts || 0} / {call.max_connect_retries || 3}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Next Retry</label>
                  <strong>{call.next_retry_at ? new Date(call.next_retry_at).toLocaleString() : '—'}</strong>
                </div>
                <div className="col-6">
                  <label className="text-muted small d-block">Initiated</label>
                  <small>{new Date(call.created_at).toLocaleString()}</small>
                </div>
                {call.connected_at && (
                  <div className="col-6">
                    <label className="text-muted small d-block">Connected</label>
                    <small>{new Date(call.connected_at).toLocaleString()}</small>
                  </div>
                )}
                {call.call_sid && (
                  <div className="col-12">
                    <label className="text-muted small d-block">Call SID</label>
                    <code className="small">{call.call_sid}</code>
                  </div>
                )}
              </div>

              {/* DTMF History */}
              {call.dtmf_history && call.dtmf_history.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-bold">
                    <i className="bi bi-hash me-1"></i> DTMF Inputs
                  </h6>
                  <div className="list-group list-group-flush">
                    {call.dtmf_history.map((d, i) => (
                      <div key={i} className="list-group-item px-0 d-flex justify-content-between">
                        <span>
                          <span className="badge bg-warning text-dark me-2">{d.digit}</span>
                          <small className="text-muted">({d.context})</small>
                        </span>
                        <small className="text-muted">
                          {new Date(d.timestamp).toLocaleTimeString('en-US', { hour12: false })}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call Timeline */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="fw-bold mb-0">
                  <i className="bi bi-clock-history me-2 text-primary"></i>
                  Call Timeline
                </h5>
                {call.call_status !== 'completed' && call.call_status !== 'disconnected' && call.call_status !== 'failed' && call.call_status !== 'unable_to_connect' && (
                  <span className="d-flex align-items-center gap-2">
                    <span className="live-pulse"></span>
                    <small className="text-success fw-bold">LIVE</small>
                  </span>
                )}
              </div>
              <CallTimeline events={events} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallDetail;
